import { Card, CardBody, Col } from "reactstrap";
import { H5, Image, LI, SVG, UL } from "../../../../../AbstractElements";
import { badgeLinks } from "../table/TeamMembersTable";
import { Link } from "react-router-dom";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import { memo, useEffect, useState } from "react";
import SkeletonCircleLoader from "../../../../../CommonElements/Loader/SkeletonCircleLoader";
import SkeletonLoader from "../../../../../CommonElements/Loader/SkeletonLoader";
import { useInViewPort } from "../../../../../hooks/useInViewport";
import { getUserImg } from "../../../../../utils/getUserImg";
import "../../../../../utils/animation.scss";
import { ITeamMember, ProbationTeamMember } from "../../types";
import useIsMobile from "../../../../../hooks/useInMobile";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { fetchSurveyFormResponse } from "../../../../surveyForms/FormResponse/redux/slice/SurveyFormResponseSlice";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { employeeUpdateProbationModal } from "../../../../../utils/Constant";
import { AppDispatch } from "../../../../../ReduxToolkit/Store";
import {
  setEmployeePrimaryTabActive,
  setEmployeeSecondaryTabActive,
} from "../../../../employeeManagement/redux/slice/EmployeeManagementSlice";

export interface ITeamMembersCardProps<
  T extends ITeamMember | ProbationTeamMember
> {
  dispatch: AppDispatch;
  loading: boolean;
  type: "teamMember" | "probationMember";
  teamMembersDetails: T;
  setSelectedUser?: (user: any) => void; // only needed for probation
}

const TeamMembersCard = <T extends ITeamMember | ProbationTeamMember>({
  type,
  loading,
  dispatch,
  setSelectedUser,
  teamMembersDetails,
}: ITeamMembersCardProps<T>) => {
  const { observerRef, inView } = useInViewPort<HTMLDivElement>({
    threshold: 0.1,
    triggerOnce: true,
  });
  const isMobile = useIsMobile();

  const { id, name, email, profile, gender } = teamMembersDetails;

  const src = getUserImg(profile, gender);

  const [animate, setAnimate] = useState<boolean>(false);

  const handleEditClick = () => {
    if (type === "probationMember" && setSelectedUser) {
      const selected = {
        user_id: (teamMembersDetails as ProbationTeamMember).id,
        email: (teamMembersDetails as ProbationTeamMember).email,
        name: (teamMembersDetails as ProbationTeamMember).name,
        gender: (teamMembersDetails as ProbationTeamMember).gender,
        profile: (teamMembersDetails as ProbationTeamMember).profile,
        doj: (teamMembersDetails as ProbationTeamMember).doj,
        probation_date: (teamMembersDetails as ProbationTeamMember)
          .probation_date,
        extended_date: (teamMembersDetails as ProbationTeamMember).extended_date,
        department_name: (teamMembersDetails as ProbationTeamMember)
          .department_name,
        probation_status: (teamMembersDetails as ProbationTeamMember)
          .probation_status,
        probation_remark: (teamMembersDetails as ProbationTeamMember)
          .probation_remark,
        emp_code: (teamMembersDetails as ProbationTeamMember).emp_code,
        form_id: (teamMembersDetails as ProbationTeamMember).form_id,
      };

      if (selected.user_id && selected.form_id) {
        dispatch(
          fetchSurveyFormResponse({
            formId: Number(selected.form_id),
            userId: Number(selected.user_id),
          })
        );
      }

      setSelectedUser(selected);
      dispatch(openModal({ modalType: employeeUpdateProbationModal }));
    }
  };

  const handleOnClick = (primaryTab: number, secondaryTab: number) => {
    dispatch(
      setEmployeePrimaryTabActive({ tabId: primaryTab, employee: null })
    );
    dispatch(setEmployeeSecondaryTabActive(secondaryTab));
  };

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setAnimate(true), 150);
      return () => clearTimeout(timer);
    } else {
      setAnimate(false);
    }
  }, [loading]);

  return (
    <Col key={id} xl="4" sm="6" xxl="4" className="col-ed-4 box-col-4 pe-sm-0">
      <Card
        className={`social-profile ${
          inView ? "fadeInUp animate" : "opacity-0"
        }`}
        innerRef={observerRef}
      >
        <CardBody className="p-2">
          {loading ? (
            <div className="w-100 d-flex justify-content-center">
              <SkeletonCircleLoader size={100} />
            </div>
          ) : (
            <div className="social-img-wrap">
              <div className="social-img">
                <Image src={src} alt="user" />
              </div>
              <div className="edit-icon">
                <SVG iconId="profile-check" />
              </div>
            </div>
          )}

          <div className="social-details">
            {loading ? (
              <div className="d-flex justify-content-center">
                <SkeletonLoader height={10} width={75} />
              </div>
            ) : (
              <H5
                className={`mb-1 f-14 title-animate ${animate ? "show" : ""}`}
              >
                {name}
              </H5>
            )}

            {loading ? (
              <div className="d-flex justify-content-center">
                <SkeletonLoader height={10} width={150} />
              </div>
            ) : (
              <H5
                className={`f-light f-12 title-animate ${
                  animate ? "show" : ""
                }`}
              >
                {email}
              </H5>
            )}
            {type === "probationMember" && (
              <div className="f-12 f-light mt-1">
                <div
                  className={`d-flex ${
                    isMobile
                      ? "flex-column align-items-center"
                      : "justify-content-center"
                  } mt-2`}
                >
                  <div
                    className={`d-flex justify-content-center title-animate ${
                      animate ? "show" : ""
                    }`}
                  >
                    {loading && <SkeletonLoader height={15} width={150} />}
                    {!loading &&
                      "extended_date" in teamMembersDetails &&
                      teamMembersDetails.extended_date && (
                        <>
                          <strong>Extended Date: </strong>{" "}
                          {teamMembersDetails.extended_date}
                        </>
                      )}

                    {!loading &&
                      !("extended_date" in teamMembersDetails) &&
                      "probation_date" in teamMembersDetails && (
                        <>
                          <strong>Probation End Date: </strong>{" "}
                          {teamMembersDetails.probation_date ?? "NA"}
                        </>
                      )}
                  </div>
                  {!loading && (
                    <IconComponent
                      className={`fa fa-edit f-20 text-primary pointer ms-3 ${
                        isMobile ? "mt-2" : ""
                      }`}
                      onClick={handleEditClick}
                    />
                  )}
                </div>
              </div>
            )}
            <UL className="social-follow flex-row simple-list mb-3 mt-3">
              {badgeLinks.map(({ color, text, primaryTab, secondaryTab }) => (
                <LI key={`${primaryTab}-${secondaryTab}`}>
                  <Link
                    to={`/user-management/user/${id}`}
                    onClick={() => handleOnClick(primaryTab, secondaryTab)}
                  >
                    <BadgeDetails
                      color={color}
                      text={text}
                      className={`rounded-pill pointer title-animate ${
                        animate ? "show" : ""
                      }`}
                      isBadgeLoading={loading}
                    />
                  </Link>
                </LI>
              ))}
            </UL>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default memo(TeamMembersCard);
